import { useDispatch, useSelector } from "react-redux";
import { Input } from "antd";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";
import { useTranslation } from 'react-i18next';

import { addToCart, fetchCartProducts, reduceFromCart } from "store/slices/cartSlice";
import { setCartPopupOpen } from "store/slices/layoutSlice";
import useCustomer from "hooks/useCustomer";
import useGuardedAddToCart from "hooks/useGuardedAddToCart";
import { getStock, getMaxAvailableInCartUnit } from "helpers/product";
import Icon from "components/Icon";

const AddToCartButton = ({ item, className = "popular-product-button" }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { customer } = useCustomer();
  const { guardedAdd } = useGuardedAddToCart();
  const { data } = useSelector((state) => state.cart.cartProducts);
  const inCart = data.products?.find((p) => +p.product_id === +item.product_id);
  const stock = getStock(item);
  const isDisabled = stock.quantity === 0 && stock.stock === 0;

  const performAdd = async (quantity) => {
    await addToCart(
      item.product_id,
      quantity,
      customer.token ? undefined : item
    );
    dispatch(fetchCartProducts({ refetchAfterMutation: true }));
    dispatch(setCartPopupOpen(true));
  };

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    const qty = item.minimum || 1;
    await guardedAdd({
      item,
      addQty: qty,
      inCartQty: 0,
      onConfirm: () => performAdd(qty),
    });
  };

  const handleReduceToCart = async (e) => {
    e.stopPropagation();
    await reduceFromCart(item.product_id, 1);
    dispatch(fetchCartProducts({ refetchAfterMutation: true }));
    dispatch(setCartPopupOpen(true));
  };

  const handleIncreaseToCart = async (e) => {
    e.stopPropagation();
    const maxAvailable = getMaxAvailableInCartUnit(item);
    const qty = Number(inCart.quantity) || 0;
    const step = Number(item.minimum) || 1;
    if (qty + step > maxAvailable) return;
    await guardedAdd({
      item,
      addQty: step,
      inCartQty: qty,
      onConfirm: () => performAdd(step),
    });
  };

  if (inCart) {
    const maxAvailable = getMaxAvailableInCartUnit(item);
    const qty = Number(inCart.quantity) || 0;
    const step = Number(item.minimum) || 1;
    const plusDisabled = qty + step > maxAvailable;
    return (
      <div className="add-to-cart-quantity-group" style={{ width: "100%", display: "flex", marginBottom: "16px" }}>
        <button
          data-click-key={`cart:minus:${item.product_id}`}
          onClick={handleReduceToCart}
          className={className}
          style={{
            height: "48px",
            width: "48px",
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderTopLeftRadius: "8px",
            borderBottomLeftRadius: "8px",
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            marginBottom: 0,
            flexShrink: 0,
            border: "none",
          }}
        >
          <MinusOutlined style={{ fontSize: 20, color: "#fff" }} />
        </button>
        <Input
          value={`${inCart.quantity} ${item.main_unit || "шт"}`}
          readOnly
          style={{
            height: "48px",
            textAlign: "center",
            flex: "1",
            borderRadius: 0,
            borderLeft: "none",
            borderRight: "none",
            fontFamily: "'TT Firs Neue', sans-serif",
            fontSize: "16px",
            fontWeight: 600,
          }}
        />
        <button
          data-click-key={`cart:plus:${item.product_id}`}
          onClick={handleIncreaseToCart}
          disabled={plusDisabled}
          className={className}
          style={{
            height: "48px",
            width: "48px",
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderTopRightRadius: "8px",
            borderBottomRightRadius: "8px",
            marginBottom: 0,
            flexShrink: 0,
            border: "none",
          }}
        >
          <PlusOutlined style={{ fontSize: 20, color: "#fff" }} />
        </button>
      </div>
    );
  }

  return (
    <button
      className={className}
      data-click-key={`cart:add:${item.product_id}`}
      onClick={handleAddToCart}
      disabled={isDisabled}
      style={{ width: "100%" }}
    >
      <Icon name="icon-shopping-cart" className="popular-product-button-icon" />
      <span>{t('cart.addToCart')}</span>
    </button>
  );
};

export default AddToCartButton;

